import React, { useState, useEffect } from 'react';
import { InoRowProps } from './InoLayout.types';
import { useMappedKeydown } from '../../hooks/useMappedKeydown';
import { InoElementWrapper } from './InoElementWrapper';
import { hasInoColChildren } from './utils';
import { InoCol } from './InoCol';

export const InoRow: React.FC<InoRowProps> = ({
  children,
  isActive = false,
  infinite = false,
  classNames = '',
  initialActiveIndex = 0,
  onActiveChange,
  onUp,
  onDown,
  onLeft,
  onRight,
  onOk,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const childrenArray = React.Children.toArray(children);
  const hasColChildren = hasInoColChildren(children);

  useEffect(() => {
    if (onActiveChange) {
      onActiveChange(activeIndex);
    }
  }, [activeIndex, onActiveChange]);

  const handleNavigation = (direction: 'left' | 'right') => {
    if (!isActive || hasColChildren) return;

    setActiveIndex((prev) => {
      if (direction === 'left') {
        if (prev === 0 && infinite) {
          return childrenArray.length - 1;
        }
        return Math.max(0, prev - 1);
      } else {
        if (prev === childrenArray.length - 1 && infinite) {
          return 0;
        }
        return Math.min(childrenArray.length - 1, prev + 1);
      }
    });
  };

  useMappedKeydown({
    isActive,
    onLeft: (e) => {
      if (hasColChildren) {
        onLeft?.(e, activeIndex);
      } else if (activeIndex === 0 && !infinite && onLeft) {
        onLeft(e, activeIndex);
      } else {
        handleNavigation('left');
      }
    },
    onRight: (e) => {
      if (hasColChildren) {
        onRight?.(e, activeIndex);
      } else if (
        activeIndex === childrenArray.length - 1 &&
        !infinite &&
        onRight
      ) {
        onRight(e, activeIndex);
      } else {
        handleNavigation('right');
      }
    },
    onUp,
    onDown,
    onOk: (e) => {
      if (onOk) {
        onOk(e, activeIndex);
      }
    },
  });

  return (
    <div className={`ino-row ${classNames}`}>
      {React.Children.map(children, (child, idx) => {
        if (!React.isValidElement(child)) return child;

        if (child.type === InoCol) {
          return React.cloneElement(child, {
            // @ts-expect-error
            ...child.props,
            isActive: isActive && idx === activeIndex,
            index: idx,
          });
        }

        // @ts-expect-error
        if ('isActive' in child.props) {
          return React.cloneElement(child, {
            ...child.props,
            // @ts-expect-error
            isActive: isActive && idx === activeIndex,
            index: idx,
          });
        }

        return (
          <InoElementWrapper
            isActive={isActive && idx === activeIndex}
            index={idx}
          >
            {child}
          </InoElementWrapper>
        );
      })}
    </div>
  );
};
